import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/services/firebase/firebase";
import { updateAppointment, updateAppointmentRequestStatus } from "./crud";
import { getCurrentUser } from "./utils";
import { createTimestamp } from "@/utils/dateUtils";
import type { AnonymousAppointmentRequest } from "./types";

/**
 * Submit an appointment request from the public website
 */
export const submitAppointmentRequest = async (
  requestData: AnonymousAppointmentRequest
): Promise<{ success: boolean; message: string; requestId?: string }> => {
  const submitRequest = httpsCallable(functions, "submitAppointmentRequest");

  try {
    const result = await submitRequest(requestData);
    return result.data as {
      success: boolean;
      message: string;
      requestId?: string;
    };
  } catch (error: any) {
    console.error("Error submitting appointment request:", error);
    throw Object.assign(new Error(error.message || "Failed to submit appointment request"), { details: error.details });
  }
};

/**
 * Accept an appointment request and create a scheduled appointment
 */
export const acceptAppointmentRequest = async (
  lawyerUid: string,
  requestId: string
): Promise<void> => {
  try {
    const acceptRequest = httpsCallable(functions, "acceptAppointmentRequest");
    await acceptRequest({ requestId, lawyerUid });

    // Update local request status
    await updateAppointmentRequestStatus(requestId, "accepted");
  } catch (error: any) {
    console.error("Error accepting appointment request:", error);
    throw new Error(error.message || "Failed to accept appointment request");
  }
};

/**
 * Cancel an appointment request
 */
export const cancelAppointmentRequest = async (
  lawyerUid: string,
  requestId: string,
  reason: string = ""
): Promise<void> => {
  try {
    const cancelRequest = httpsCallable(functions, "cancelAppointmentRequest");
    await cancelRequest({
      lawyerUid,
      requestId,
      reason: reason || "Cancelled by lawyer",
    });

    // Update local request status
    await updateAppointmentRequestStatus(requestId, "cancelled", reason);
  } catch (error: any) {
    console.error("Error cancelling appointment request:", error);
    throw new Error(error.message || "Failed to cancel appointment request");
  }
};

/**
 * Update the preferred date/time for an appointment request (for pending requests)
 */
export const rescheduleAppointmentRequest = async (
  id: string,
  newDate: string,
  newTime: string
): Promise<void> => {
  const user = getCurrentUser();

  // Use the CRUD function to update the request
  await updateAppointmentRequestStatus(id, "rescheduled");

  // Also update the preferred dates (we need to extend the updateAppointmentRequestStatus function)
  // For now, let's use the direct Firestore update like the original
  const { doc, updateDoc } = await import("firebase/firestore");
  const { requestsCol } = await import("./utils");

  await updateDoc(doc(requestsCol(user.uid), id), {
    preferredDate: newDate,
    preferredTime: newTime,
    status: "rescheduled",
  });
};

/**
 * Request to reschedule an existing appointment
 */
export const requestAppointmentReschedule = async (
  id: string,
  newDate: string,
  newTime: string
): Promise<string> => {
  const user = getCurrentUser();

  try {
    const rescheduleRequest = httpsCallable(
      functions,
      "requestAppointmentReschedule"
    );
    const result = await rescheduleRequest({
      appointmentId: id,
      newDate,
      newTime,
      lawyerId: user.uid,
    });

    // Update appointment status to indicate pending reschedule
    await updateAppointment(id, {
      status: "reschedule_requested",
      rescheduleReason: `Reschedule to ${newDate} at ${newTime}`,
      updatedAt: createTimestamp(),
    });

    return result.data as string;
  } catch (error: any) {
    console.error("Error requesting reschedule:", error);
    throw new Error(error.message || "Failed to request reschedule");
  }
};

/**
 * Confirm a reschedule request (called by cloud function from email link)
 */
export const confirmAppointmentReschedule = async (
  appointmentId: string,
  newDate: string,
  newTime: string
): Promise<void> => {
  // This will be called by the Cloud Function when client clicks email links
  const confirmReschedule = httpsCallable(
    functions,
    "confirmAppointmentReschedule"
  );

  try {
    await confirmReschedule({
      appointmentId,
      newDate,
      newTime,
    });
  } catch (error: any) {
    console.error("Error confirming reschedule:", error);
    throw new Error(error.message || "Failed to confirm reschedule");
  }
};

/**
 * Complete an appointment
 */
// ...existing code...

/**
 * Cancel an appointment
 */
export const cancelAppointment = async (
  id: string,
  reason?: string,
  cancelledBy?: "client" | "lawyer",
  lawyerUid?: string
): Promise<void> => {
  await auth.authStateReady();
  const actor = cancelledBy === "lawyer" ? "lawyer" : "client";
  const user = actor === "lawyer" ? getCurrentUser() : auth.currentUser;
  // Fail locally if token refresh fails; the callable SDK otherwise omits auth.
  if (user) {
    await user.getIdToken();
    if (auth.currentUser !== user) throw new Error("Authentication changed. Please try again.");
  }
  const actualLawyerUid = lawyerUid || (actor === "lawyer" ? user?.uid : undefined);
  if (!actualLawyerUid) throw new Error("Missing lawyerUid for appointment cancellation.");

  try {
    const cancelAppointment = httpsCallable(functions, "cancelAppointment");
    await cancelAppointment({
      appointmentId: id,
      reason: reason || "Cancelled",
      cancelledBy: actor,
      lawyerUid: actualLawyerUid,
    });

  } catch (error: any) {
    console.error("Error cancelling appointment:", error);
    throw error;
  }
};

/**
 * Reschedule an existing appointment (for scheduled appointments)
 */
export const rescheduleScheduledAppointment = async (
  appointmentId: string,
  newDate: string,
  newTime: string
): Promise<string> => {
  const user = getCurrentUser();

  try {
    const rescheduleAppointment = httpsCallable(
      functions,
      "rescheduleAppointment"
    );
    const result = await rescheduleAppointment({
      appointmentId,
      newDate,
      newTime,
      lawyerId: user.uid,
    });

    return result.data as string;
  } catch (error: any) {
    console.error("Error rescheduling appointment:", error);
    throw new Error(error.message || "Failed to reschedule appointment");
  }
};
